import React, { useState, useEffect } from "react";

import {
  Button,
  Dialog,
  Divider,
  TextField,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from "@material-ui/core";

import { ThemeProvider } from "@material-ui/core/styles";

import {
  Close,
  Drafts,
  ArrowForwardIos,
  MailOutlineOutlined,
} from "@material-ui/icons";

import { themeSelector } from "../../design/theme";

import { Auth0Lock } from "auth0-lock";
import PropTypes from "prop-types";
import { Alert as BsAlert } from "reactstrap";
import logo from "../../images/blumira_login_blue.png";
import microsoftBlackLogo from "../../images/microsoftBlackLogo.png";
import { auth, AUTH_STATUS } from "../../lib/auth";
import config from "../../lib/config";
import { storageAvailable } from "../../lib/helpers";
import Logger from "../../lib/logger";
import { scopeSetUser } from "../../lib/sentry";
import OutageBanner from "../Components/OutageBanner";
import ReCAPTCHA from "react-google-recaptcha";
import { RecaptchaAPI, SignUpAPI } from "../../lib/api";
import { validateEmail } from "views/Components/Form";

import { loginViewStyles } from "./loginViewStyles";

const API = {
  recaptcha: new RecaptchaAPI(),
  signUp: new SignUpAPI(),
};

const logger = Logger("Login View");

/*
Constanst and Helpers
*/

const STORAGE_TYPES_IN_USE = ["localStorage", "sessionStorage"];

// Exracts the user ID from the Auth0 authResult
const getUserId = (authResult) => {
  const { idTokenPayload } = authResult;

  const { auth = {} } = config;
  const { jwt = {} } = auth;
  const { namespace } = jwt;

  let userId = "Unknown";

  if (idTokenPayload && namespace) {
    userId = idTokenPayload[`${namespace}/person_id`] || "No person_id in JWT";
  }

  return userId;
};

/*
Component
*/

const greenColorValue = "#3BB273";
const whiteColorValue = "#FFFFFF";

const makeLock = (connection, container, query, displayName) => {
  return new Auth0Lock(config.auth.auth0.client_id, config.auth.auth0.domain, {
    avatar: null,
    allowedConnections: [connection],
    configurationBaseUrl: "https://cdn.auth0.com",
    container: container,
    auth: {
      sso: false,
      responseType: "id_token",
      audience: config.auth.auth0.audience,
      params: {
        scope: "openid profile",
      },
    },
    initialScreen: query.supportForgotPassword ? "forgotPassword" : "login",
    prefill: {
      email: query.email,
    },
    autoclose: true,
    languageDictionary: {
      enterpriseLoginIntructions: "",
      loginWithLabel: "Continue with %s",
      forgotPasswordTitle: "Set or Reset your password",
      forgotPasswordAction: "Set or Reset Password",
      forgotPasswordInstructions:
        "Please enter your email address. " +
        "We will send you an email to set or reset your password.",
    },
    theme: {
      primaryColor: whiteColorValue,
      logo: "https://blu-public-assets.storage.googleapis.com/images/blu-logo.png",
      authButtons: {
        "MS-SSO": {
          displayName: displayName,
          primaryColor: whiteColorValue,
          foregroundColor: "#000",
          icon: microsoftBlackLogo,
        },
        "Username-Password-Authentication": {
          primaryColor: greenColorValue,
          foregroundColor: whiteColorValue,
        },
      },
    },
    // Disable the 'grey box' prefilled login
    rememberLastLogin: false,
  });
};

const theme = themeSelector("default");

const LoginView = (props) => {
  const classes = loginViewStyles();

  const recaptchaRef = React.useRef();

  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [headerMsg, setHeaderMsg] = useState("");
  const [firstTime, setFirstTime] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [storageAlert, setStorageAlert] = useState(false);
  const [isSignupDialogOpen, setIsSignupDialogOpen] = useState(false);
  const [isResponseSuccessful, setIsResponseSuccessful] = useState(false);

  let lock;
  let ssoLock;

  useEffect(() => {
    logger.log("Login view mounted");

    const storageOK = STORAGE_TYPES_IN_USE.reduce(
      (acc, cur) => acc && storageAvailable(cur),
      true
    );

    setStorageAlert(!storageOK);

    if (auth.currentStatus() === AUTH_STATUS.LOGGED_IN)
      return setIsLoggingIn(true);

    const { query = {} } = props;

    /*
      If the user is coming from the initial welcome email, then query.message
      will start with 'Your email was verified'.
      We will set a flag called 'firstTime' in the state so that if other UI
      customizations need to be made, they can refer to the flag.
    */
    if (query.message && query.message.startsWith("Your email was verified.")) {
      setFirstTime(true);
      setHeaderMsg(
        "Please complete account creation by using the form below. \nYour " +
          "email must be the same as in the Blumira invite you just clicked."
      );
    }

    lock = makeLock("Username-Password-Authentication", "login-root", query);
    lock.on("authenticated", onAuthenticated);
    lock.on("authorization_error", onAuthError);
    lock.on("forgot_password submit", onForgotPasswordSubmit);

    ssoLock = makeLock("MS-SSO", "sso-root", query, "Continue with Microsoft");
    ssoLock.on("authenticated", onAuthenticated);
    ssoLock.on("authorization_error", onAuthError);
    ssoLock.on("forgot_password submit", onForgotPasswordSubmit);

    if (query.logout === "true") {
      // Logout from auth0
      lock.logout({
        returnTo: window.location.origin,
      });

      // Clear the logout='true' query
      props.dispatchClearLogoutQuery();

      // Unset the Sentry scope.user
      scopeSetUser(null);
    }

    lock.show();
    ssoLock.show();
  }, []);

  const onForgotPasswordSubmit = () => {
    if (firstTime) {
      setHeaderMsg(
        "Please check your email" +
          " to finish setting your initial Blumira password."
      );
    } else {
      setHeaderMsg(
        "If an account with this email already exists, you’ll receive a password reset email shortly. " +
          'If you don’t yet have an account, please click "Sign up with email" below to create your account.'
      );
    }
    document.getElementById("login-root").style.display = "none";
    document.getElementById("welcome").style.visibility = "hidden";
  };

  const onAuthError = (error) => {
    const message = "Auth0 login error:";
    const errMsg = `${message} ${JSON.stringify(error)}`;

    logger.error(message, new Error(errMsg));

    lock.hide();
    lock.show({
      flashMessage: {
        type: "error",
        text: error.errorDescription || error.description,
      },
    });
  };

  const handleAuthenticatedUI = () => {
    // show user progress on logging in
    setHeaderMsg("Logging into your account...");
    setIsLoggingIn(true);

    // completely hide auth0 component
    lock.hide();
  };

  const toggleSignupDialog = () => {
    setIsSignupDialogOpen(!isSignupDialogOpen);

    // reset state vars to ensure a fresh experience
    setEmail("");
    setIsResponseSuccessful(false);
  };

  const onAuthenticated = (authResult) => {
    logger.log("User succesfully authenticated with Auth0");

    handleAuthenticatedUI();

    const userId = getUserId(authResult);
    scopeSetUser({ userId });

    let redirectPayload = null;
    try {
      redirectPayload = JSON.parse(sessionStorage.getItem("payload"));
      sessionStorage.removeItem("payload");
    } catch (err) {
      logger.error("Failed to read payload from Session Storage.", err);
    }

    // Login to Blumira
    props.dispatchLoginToBlumira({ authResult, redirectPayload });
  };

  const handleChange = (event) => {
    const email = event.target.value;
    if (validateEmail(event.target.value)) {
      setError("");
    }
    setEmail(email);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (email === "" || !validateEmail(email)) {
      setError("Email is not valid");
      return;
    }

    try {
      const recaptchaToken = await recaptchaRef.current.executeAsync();
      const signUpResponse = await API.signUp.create({
        email,
        first_name: "",
        last_name: "",
        org_roles: [],
        locations: [],
        recaptcha_token: recaptchaToken,
      });

      setIsResponseSuccessful(signUpResponse.success);
    } catch (e) {
      recaptchaRef.current.reset();
      setError(e.toString());
      setEmail("");
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div
        className={`h-100 w-100 d-flex align-items-center login`}
        style={{ flexFlow: "column" }}
      >
        <Dialog open={isSignupDialogOpen} onClose={toggleSignupDialog}>
          <div className={classes.dialogHeaderContainer}>
            <DialogTitle>Create new account</DialogTitle>
            <Close className={classes.closeIcon} onClick={toggleSignupDialog} />
          </div>
          <DialogContent>
            {isResponseSuccessful ? (
              <div className={classes.dialogContent}>
                <Drafts className={classes.draftsIcon} fontSize={"large"} />
                <p className={classes.primarySuccessText}>Check your inbox</p>
                <p>
                  We sent you an email that includes instructions on how to
                  complete activation
                </p>
                <p
                  className={classes.ctaSuccessText}
                  onClick={toggleSignupDialog}
                >
                  Okay, got it
                </p>
              </div>
            ) : (
              <div className={classes.dialogContent}>
                {/* 
                  TODO: find a way to show MS SSO lock in dialog
                  <div id='sso-root' style={{ display: storageAlert ? 'none' : 'block' }}/> 
                */}
                <TextField
                  className={classes.textField}
                  type={"text"}
                  value={email}
                  error={error !== ""}
                  onChange={handleChange}
                  helperText={error ? error : ""}
                  placeholder={"csmith@acmeorg.com"}
                  InputProps={{
                    startAdornment: (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: "#f1f1f1",
                          padding: "10px",
                        }}
                      >
                        <MailOutlineOutlined fontSize="small" color="primary" />
                      </div>
                    ),
                  }}
                />
                <Button
                  variant={"contained"}
                  onClick={handleSubmit}
                  endIcon={<ArrowForwardIos />}
                  className={classes.continueButton}
                >
                  Create new account
                </Button>
                <Divider className={classes.signupDialogDivider} />
                <div>
                  <span className={classes.microsoftLogoContainer}>
                    <img
                      alt={"Microsoft Logo"}
                      src={microsoftBlackLogo}
                      className={classes.microsoftLogo}
                    />
                    <p className={classes.microsoftTitleText}>Microsoft</p>
                  </span>
                  <p className={classes.microsoftSecondaryText}>
                    Use your Microsoft 365 credentials to create an
                  </p>
                  <p className={classes.microsoftSecondaryText}>
                    account by closing this dialog and clicking:
                  </p>
                  <p className={classes.microsoftSecondaryText}>
                    "Continue with Microsoft"
                  </p>
                </div>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  size={"invisible"}
                  sitekey={config.recaptcha_site_key}
                />
              </div>
            )}
          </DialogContent>
        </Dialog>

        <div className="row mx-auto align-items-center">
          <div
            style={{ textAlign: "center" }}
            className="col align-items-center mx-auto"
          >
            <img
              src={logo}
              alt={"Logo"}
              title={"Blumira"}
              className={classes.logoImage}
            />

            <OutageBanner />

            <p className={classes.headerMsg}>{headerMsg}</p>

            {storageAlert && (
              <div>
                <BsAlert color="info">
                  <div>Your browser cookies and storage are disabled.</div>
                  <h2 className="alert-subtitle">
                    Please enable browser cookies and storage
                  </h2>
                  <div className="alert-subtitle">and refresh the page</div>
                </BsAlert>
              </div>
            )}
          </div>
        </div>

        <form
          className="mx-auto"
          onSubmit={(e) => {
            e.preventDefault();
          }}
          style={{ paddingBottom: "40px", marginTop: -10 }}
        >
          <div
            className="col align-items-center"
            style={{
              textAlign: "center",
              boxSizing: "border-box",
              padding: "0px",
            }}
          >
            {props.error && props.error.message && (
              <div className="alert alert-danger" role="alert">
                {props.error.message}
              </div>
            )}

            {!!isLoggingIn && <CircularProgress />}

            {!isLoggingIn && (
              <div style={{ marginTop: 15 }}>
                <Button
                  disableRipple
                  disableFocusRipple
                  onClick={toggleSignupDialog}
                  className={classes.createAccountButton}
                >
                  Create new account
                </Button>

                <p
                  style={{
                    fontSize: 18,
                    paddingTop: 15,
                    fontWeight: "500",
                  }}
                >
                  or
                </p>
              </div>
            )}

            {/* MICROSOFT SSO LOCK */}
            <div
              id="sso-root"
              style={{
                display: isLoggingIn || storageAlert ? "none" : "block",
              }}
            />

            {/* USERNAME/PASSWORD LOGIN LOCK */}
            <div className="form-group" style={{ marginTop: "10px" }}>
              <div
                id="login-root"
                style={{
                  display: storageAlert ? "none" : "block",
                }}
              />
            </div>

            <div style={{ fontSize: "10px", marginTop: "20px" }}>
              {config.deploymentType === "development" ||
              config.deploymentType === "local-dev" ? (
                <div className="dev-diagnostics">
                  <div>
                    <p>{`build: ${config.build_tag}`}</p>
                    <p>{`auth0_client_id: ${config.auth.auth0.client_id}`}</p>
                    <p>{`auth0 domain: ${config.auth.auth0.domain}`}</p>
                    <p>{`FEA URL: ${config.api}`}</p>
                  </div>
                </div>
              ) : (
                <div>
                  <p>{`build: ${config.build_tag}`}</p>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </ThemeProvider>
  );
};

LoginView.propTypes = {
  query: PropTypes.shape({
    logout: PropTypes.string,
    message: PropTypes.string,
  }),
  error: PropTypes.shape({ message: PropTypes.string }),
  dispatchClearLogoutQuery: PropTypes.func.isRequired,
  dispatchLoginToBlumira: PropTypes.func.isRequired,
};

LoginView.defaultProps = {
  query: {
    logout: "false",
  },
  error: null,
};

export default LoginView;
